import React, {
  createContext, useContext, useCallback, useEffect, useMemo, useState,
} from 'react';

import {
  Session, CaseMasterPlans, getCaseMasterPlan, isB2cCaseMasterUser,
  hasFullCaseMasterAccess, getCaseMasterLockInfo,
} from 'utils';
import { getCaseMasterProblems, getRecentCaseMasterAttempts } from 'services';

/**
 * Shared Case Master access state.
 *
 * For B2C free users, gating needs two pieces of data that aren't in metadata:
 *   1. The flagged free case (`GET /casemasterProblem?isFreeCase=true&isActive=true`).
 *   2. Whether the single free attempt is spent (any recent attempt with status
 *      `feedback_generated` — the backend no longer meters attempts).
 *
 * Everyone else (non-B2C, Premium, active/expired Pro) is decided synchronously
 * from metadata, so no fetch happens for them. The data is loaded once here and
 * shared so the dashboard's cards/topics/recent-attempts don't each refetch.
 */
const CaseMasterAccessContext = createContext(null);

/**
 * Core hook: resolves access state, fetching the free-case + attempt data only
 * when the user is actually a B2C free user. Used by the provider and directly
 * by screens that live outside the provider (e.g. the performance page).
 */
export const useCaseMasterAccessData = () => {
  const fullAccess = useMemo(() => hasFullCaseMasterAccess(), []);
  const isFree = useMemo(
    () => isB2cCaseMasterUser() && getCaseMasterPlan() === CaseMasterPlans.FREE,
    []
  );

  const [loading, setLoading] = useState(isFree);
  const [freeCaseId, setFreeCaseId] = useState(null);
  const [freeCaseTopic, setFreeCaseTopic] = useState(null);
  const [freeAttemptUsed, setFreeAttemptUsed] = useState(false);

  useEffect(() => {
    if (!isFree) return undefined;

    let active = true;

    (async () => {
      try {
        setLoading(true);

        const [problemsRes, { attempts }] = await Promise.all([
          getCaseMasterProblems({ isFreeCase: true, isActive: true }),
          getRecentCaseMasterAttempts({ user: Session.userId }),
        ]);

        if (!active) return;

        const freeCase = problemsRes?.casemasterProblems?.[0] || null;
        setFreeCaseId(freeCase?._id || null);
        setFreeCaseTopic(freeCase?.topic || null);

        setFreeAttemptUsed(
          Array.isArray(attempts) &&
          attempts.some((attempt) => attempt?.status === 'feedback_generated')
        );
      } catch (err) {
        // Fail closed: leave freeCaseId null so the free case stays locked.
        console.error('[CaseMasterAccess] failed to resolve free-plan access:', err?.message);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [isFree]);

  const getLockInfo = useCallback(
    (caseId) => getCaseMasterLockInfo({ caseId, freeCaseId, freeAttemptUsed }),
    [freeCaseId, freeAttemptUsed]
  );

  return useMemo(() => ({
    loading,
    fullAccess,
    isFree,
    freeCaseId,
    freeCaseTopic,
    freeAttemptUsed,
    getLockInfo,
  }), [loading, fullAccess, isFree, freeCaseId, freeCaseTopic, freeAttemptUsed, getLockInfo]);
};

export const CaseMasterAccessProvider = ({ children }) => {
  const value = useCaseMasterAccessData();

  return (
    <CaseMasterAccessContext.Provider value={value}>
      {children}
    </CaseMasterAccessContext.Provider>
  );
};

/**
 * Read shared access state. Must be used within a CaseMasterAccessProvider.
 * Components outside the provider should call useCaseMasterAccessData directly.
 */
export const useCaseMasterAccess = () => {
  const ctx = useContext(CaseMasterAccessContext);

  if (!ctx) {
    throw new Error('useCaseMasterAccess must be used within a CaseMasterAccessProvider');
  }

  return ctx;
};
